import { Component } from '@theme/component';
import { fetchCartJson, prefersReducedMotion } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

const REACHED_TEXT = '¡Ya tenés envío gratis! 🎉';

// Colores de marca (DESIGN.md §2). El accent "puro" y el "ink", la amarilla de las estrellas y la
// tinta: son partículas decorativas sin texto encima, así que acá no rige la regla de contraste
// que obliga a `#CC4B09` cuando hay texto blanco chico.
const CONFETTI_COLORS = ['#E9560C', '#CC4B09', '#FBBC04', '#1a1a1a', '#ffffff'];
const CONFETTI_COUNT = 140;
const CONFETTI_MS = 2200;
const GRAVEDAD = 0.32;

/**
 * Un festejo que quedó debiendo.
 *
 * Vive a nivel de MÓDULO y no de instancia porque el elemento se recrea: cuando el carrito pasa de
 * vacío a lleno, el morph puede reemplazar el nodo entero, y un campo de instancia se perdería
 * justo en el caso que este flag existe para cubrir.
 *
 * El caso: se agrega desde la ficha con el carrito vacío, el cajón se abre SINCRÓNICAMENTE y el
 * morph que trae la barra llega DESPUÉS — así que cuando el umbral se cruza no hay a quién
 * mostrarle nada, y para cuando la barra existe el `DrawerOpenEvent` ya pasó. Es el mismo patrón
 * que `assets/product-recommendations.js` documenta para su propio caso.
 */
let festejoPendiente = false;

/**
 * Live-updates the cart's free-shipping progress bar (see
 * `snippets/cart-free-shipping-bar.liquid`) as the cart changes, without a page reload.
 *
 * The initial paint is Liquid-only (server-rendered from `cart.total_price`, always
 * accurate at render time — unlike `variant.available`, `total_price` genuinely does
 * reflect the real cart, confirmed this project), so `connectedCallback` does nothing on
 * its own — only `StandardEvents.cartLinesUpdate` drives updates from here on, mirroring
 * the exact pattern `assets/cart-icon.js` already uses for its own live cart total.
 */
class FreeShippingBar extends Component {
  requiredRefs = ['freeShippingText', 'freeShippingFill', 'freeShippingMarker'];

  /**
   * Si el umbral YA estaba cruzado. Se siembra del `data-reached` que pinta Liquid, y de ahí en
   * más lo mantiene esta instancia.
   *
   * Sin este flag la celebración se repetiría: una sola operación de pack puede emitir dos
   * `cartLinesUpdate` (la cantidad del ancla y el regalo que se agrega al cruzar un tier), y las
   * dos pasarían por acá con `reached === true`.
   *
   * @type {boolean}
   */
  #wasReached = this.dataset.reached === 'true';

  /** @type {number | null} */
  #rafId = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    // Burbujea desde el `theme-drawer`, así que alcanza con escucharlo en `document`.
    document.addEventListener(DrawerOpenEvent.eventName, this.#alAbrirseElCajon);

    // La siembra sincrónica, que es lo que cubre el caso en que el cajón YA se abrió antes de que
    // este elemento existiera: ahí el evento de arriba no va a volver a dispararse hasta la
    // próxima apertura, y el festejo se perdería para siempre.
    if (festejoPendiente && this.#esVisible()) {
      festejoPendiente = false;
      this.#celebrar();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#alAbrirseElCajon);
    // Un rAF que siga corriendo sobre un canvas ya desprendido pinta contra la nada y no se para
    // solo.
    if (this.#rafId !== null) cancelAnimationFrame(this.#rafId);
  }

  #alAbrirseElCajon = () => {
    if (!festejoPendiente) return;
    festejoPendiente = false;
    // Un frame de aire: el evento llega con el cajón recién abierto y todavía sin caja medible.
    requestAnimationFrame(() => this.#celebrar());
  };

  onCartUpdate = (event) => {
    // El `cart` normalizado del evento no garantiza `total_price`, pero `detail.cartData` es el
    // carrito crudo que la operación ya devolvió (misma forma que `/cart.js`): se usa ése y sólo
    // se relee `/cart.js` cuando el evento viene sin él (una app externa). Mismo criterio que
    // `cart-icon.js`.
    event.promise
      ?.then(({ detail }) => detail?.cartData ?? fetchCartJson())
      .then((cartData) => {
        const thresholdCents = Number(this.dataset.thresholdCents) || 0;
        if (thresholdCents <= 0) return;

        const currentCents = cartData.total_price;
        const reached = currentCents >= thresholdCents;
        const percent = Math.min(100, Math.max(0, (currentCents * 100) / thresholdCents));

        this.refs.freeShippingFill.style.width = `${percent}%`;
        this.refs.freeShippingMarker.style.left = `${percent}%`;
        // Mirrors the exact markup Liquid renders initially (see
        // cart-free-shipping-bar.liquid) — safe as innerHTML since both pieces are our own
        // formatted number/copy, never user-supplied.
        this.refs.freeShippingText.innerHTML = reached
          ? REACHED_TEXT
          : `Estás a <strong>${formatMoney((thresholdCents - currentCents) / 100)}</strong> del envío gratis`;

        const track = this.refs.freeShippingFill.parentElement;
        track?.setAttribute('aria-valuenow', String(Math.round(percent)));

        // Sólo la TRANSICIÓN, nunca el estado. Que `reached` sea true no alcanza: el carrito se
        // re-lee en cada cambio y ya estando por encima del umbral seguiría siendo true para
        // siempre.
        if (reached && !this.#wasReached) this.#celebrar();
        this.#wasReached = reached;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[free-shipping-bar] Cart read failed:', error);
      });
  };

  /**
   * La superficie sobre la que se dibuja el festejo.
   *
   * El cajón, cuando lo hay: un `<dialog>` abierto es `position: fixed`, así que un hijo absoluto
   * lo cubre entero. Va sobre el dialog y NO sobre `.cart-drawer__inner`, que lleva
   * `overflow: hidden` y recortaría todo lo que saliera de la caja de la barra.
   *
   * En `/cart` no hay cajón: ahí el canvas va al `<body>` y se posiciona `fixed` sobre la ventana.
   * No se usa el contenedor de la barra porque un `position` heredado no está garantizado —sobre
   * un padre `static` el canvas se anclaría a cualquier ancestro posicionado y aparecería
   * corrido—, y sobre todo porque la barra mide unos 50px de alto: un festejo ahí adentro no se
   * ve. `fixed` sobre el body no depende de ningún ancestro y cubre la página entera.
   *
   * @returns {{ el: HTMLElement, fijo: boolean } | null}
   */
  #superficie() {
    const dialog = this.closest('dialog');
    if (dialog) return { el: dialog, fijo: false };
    return { el: document.body, fijo: true };
  }

  /** Si hay algo que mirar ahora mismo. */
  #esVisible() {
    const dialog = this.closest('dialog');
    // Dentro de un cajón, lo que manda es si el cajón está abierto.
    if (dialog) {
      if (!dialog.open) return false;
      const caja = dialog.getBoundingClientRect();
      return caja.width > 0 && caja.height > 0;
    }
    // En `/cart` alcanza con que la barra tenga caja: si la tiene, la página está a la vista.
    const propia = this.getBoundingClientRect();
    return propia.width > 0 && propia.height > 0;
  }

  /**
   * El festejo al cruzar el umbral. Es el único momento del carrito en el que el cliente recibe
   * algo en vez de pagar algo.
   *
   * Con `prefers-reduced-motion` no corre nada: el texto ya cambió a "¡Ya tenés envío gratis!"
   * por su cuenta, así que la información llega igual sin la animación.
   */
  #celebrar() {
    if (prefersReducedMotion()) return;

    // Si todavía no hay nada a la vista, el festejo NO se descarta: queda debiendo hasta que el
    // cajón se abra. Descartarlo era exactamente lo que hacía que no se viera al agregar desde la
    // ficha, que es el camino por el que más se cruza el umbral.
    if (!this.#esVisible()) {
      festejoPendiente = true;
      return;
    }

    this.#pulsoTexto();
    this.#confeti();
  }

  /**
   * El texto entra con una pizca de escala. Anima `transform` y no `padding`/`width`, que además
   * de ser la regla de DESIGN.md §7 evita que la barra reacomode al resto del cajón.
   */
  #pulsoTexto() {
    const texto = this.refs.freeShippingText;
    if (!texto) return;

    texto.classList.remove('free-shipping-bar__text--celebrating');
    // Reflow forzado: sin esto, quitar y volver a poner la clase en el mismo frame no reinicia la
    // animación y el segundo cruce del umbral no muestra nada.
    void texto.offsetWidth;
    texto.classList.add('free-shipping-bar__text--celebrating');
  }

  /**
   * Doble cañón desde las dos esquinas de abajo, cruzándose hacia arriba. Cubre el panel entero:
   * antes el canvas medía lo que medía la barra (~40px de alto) y el festejo pasaba desapercibido.
   *
   * @param {number} intentos Cuántos frames más esperar a que la superficie tenga caja.
   */
  #confeti(intentos = 45) {
    const superficie = this.#superficie();
    if (!superficie) return;

    // Sobre el body el tamaño es el de la ventana; dentro del cajón, el del cajón.
    const caja = superficie.fijo
      ? { width: document.documentElement.clientWidth, height: window.innerHeight }
      : superficie.el.getBoundingClientRect();

    // El cajón puede estar todavía animando su apertura y medir 0. Se reintenta por frame hasta
    // ~0,75s antes de abandonar.
    if (caja.width === 0 || caja.height === 0) {
      if (intentos > 0) this.#rafId = requestAnimationFrame(() => this.#confeti(intentos - 1));
      return;
    }

    const ancho = caja.width;
    const alto = caja.height;

    const canvas = document.createElement('canvas');
    canvas.className = superficie.fijo
      ? 'free-shipping-bar__confetti free-shipping-bar__confetti--ventana'
      : 'free-shipping-bar__confetti';
    canvas.setAttribute('aria-hidden', 'true');

    // Se topea en 2 el ratio de píxeles: más allá de eso el costo de pintar sube sin que se note,
    // y esto corre en teléfonos mientras el carrito termina de actualizarse.
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(ancho * dpr);
    canvas.height = Math.round(alto * dpr);
    canvas.style.width = `${ancho}px`;
    canvas.style.height = `${alto}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);

    // Los dos cañones, en las esquinas inferiores, apuntando hacia arriba y hacia el centro.
    const canones = [
      { x: ancho * 0.06, y: alto * 0.99, angulo: -Math.PI / 3 },
      { x: ancho * 0.94, y: alto * 0.99, angulo: (-Math.PI * 2) / 3 },
    ];

    const particulas = Array.from({ length: CONFETTI_COUNT }, (_, i) => {
      const canon = canones[i % canones.length];
      // ±25° de apertura: junta lo suficiente para leerse como un disparo, abierta lo suficiente
      // para que las dos ráfagas se crucen y llenen el panel.
      const angulo = canon.angulo + (Math.random() - 0.5) * 0.9;
      const velocidad = 16 + Math.random() * 10;
      return {
        x: canon.x,
        y: canon.y,
        vx: Math.cos(angulo) * velocidad,
        vy: Math.sin(angulo) * velocidad,
        lado: 5 + Math.random() * 6,
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        giro: Math.random() * Math.PI,
        vgiro: (Math.random() - 0.5) * 0.34,
      };
    });

    superficie.el.appendChild(canvas);

    const inicio = performance.now();
    const paso = (ahora) => {
      const t = ahora - inicio;
      if (t >= CONFETTI_MS) {
        canvas.remove();
        this.#rafId = null;
        return;
      }

      // Se desvanecen recién en el último tercio para que el estallido se lea entero.
      const desvanecer = CONFETTI_MS * 0.66;
      ctx.clearRect(0, 0, ancho, alto);
      ctx.globalAlpha = t < desvanecer ? 1 : Math.max(0, 1 - (t - desvanecer) / (CONFETTI_MS - desvanecer));

      for (const p of particulas) {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += GRAVEDAD;
        p.vx *= 0.995; // roce
        p.giro += p.vgiro;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.giro);
        ctx.fillStyle = p.color;
        // Rectángulos y no círculos: al girar, un rectángulo cambia de ancho y da la sensación de
        // papelito dando vueltas. Un círculo gira sin que se note.
        ctx.fillRect(-p.lado / 2, -p.lado / 2, p.lado, p.lado * 0.62);
        ctx.restore();
      }

      this.#rafId = requestAnimationFrame(paso);
    };

    this.#rafId = requestAnimationFrame(paso);
  }
}

/**
 * Formatea igual que el filtro `money` de Shopify, que es lo que pinta el resto del carrito.
 *
 * Antes usaba `style: 'currency'` con 0 decimales, y eso daba "$ 70.100" — con espacio
 * despues del signo y sin centavos — al lado de precios que decian "$29.900,00". Como este
 * texto se re-renderiza por JS en cada cambio del carrito, el formato cambiaba solo al
 * agregar un producto. Se arma el numero sin `style: 'currency'` (que es quien mete el
 * espacio en es-AR) y se antepone el signo a mano.
 *
 * La tienda opera unicamente en ARS y este snippet ya tiene el texto en español fijo,
 * asi que el simbolo va escrito y no derivado de la moneda del carrito.
 *
 * @param {number} amount
 */
function formatMoney(amount) {
  return `$${new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)}`;
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}
